import { useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { SlideContent, spanToClass } from '../components/synthesis/SynthPresentation';
import { ThemeId, themeClass, normalizeThemeId, getFont } from '../components/synthesis/themes';

type Share = {
  kind: 'pilotage' | 'presentation' | 'composition';
  title: string | null;
  project_name: string;
  theme_color: string;
  author_name: string | null;
  expires_at: string | null;
  payload: any;
};

declare global {
  interface Window {
    /** Par-chart readiness (indexé). Puppeteer attend que TOUS soient `true`. */
    __chartReady?: Record<number, boolean>;
    /** Agrégat final : true quand tous les charts ont fini leur rendu. */
    __chartsReady?: boolean;
  }
}

/**
 * Rendu imprimable d'une synthèse partagée. Cette page est visitée par le Worker
 * Browser Rendering (puppeteer) qui attend `window.__chartsReady === true` avant
 * de lancer page.pdf(). Le signal est calculé ci-dessous : on considère la page
 * prête quand `document.fonts.ready` a résolu, que toutes les images ont chargé,
 * que les animations CSS sont terminées, et qu'aucun chart n'est resté à zéro.
 */
export default function SharedPrintView() {
  const { token } = useParams<{ token: string }>();
  const [share, setShare] = useState<Share | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const readyTimer = useRef<number | null>(null);

  useEffect(() => {
    if (!token) return;
    fetch(`/api/v1/share/${token}`)
      .then((r) => r.json())
      .then((d: any) => {
        if (d.error) setErr(d.error);
        else setShare(d.share);
      })
      .catch((e) => setErr(String(e)));
  }, [token]);

  useEffect(() => {
    if (!share) return;
    window.__chartReady = {};
    window.__chartsReady = false;
    const charts = (share.payload?.presentation?.sections ?? []).filter((s: any) => s.type === 'chart');
    charts.forEach((_: any, i: number) => {
      window.__chartReady![i] = false;
    });

    const signalReady = async () => {
      // 1) fonts + images + animations
      try { if ((document as any).fonts?.ready) await (document as any).fonts.ready; } catch {}
      // 2) debounce final : laisse les transitions CSS finir
      await new Promise((r) => setTimeout(r, 900));
      // 3) quand pas de chart, on signale direct. Sinon on marque tous prêts après le debounce.
      if (charts.length === 0 || Object.values(window.__chartReady ?? {}).every((v) => v === true)) {
        window.__chartsReady = true;
      } else {
        // Fallback — si un chart ne s'est jamais signalé (pas instrumenté), on considère OK
        Object.keys(window.__chartReady ?? {}).forEach((k) => {
          window.__chartReady![Number(k)] = true;
        });
        window.__chartsReady = true;
      }
    };

    readyTimer.current = window.setTimeout(signalReady, 200);
    return () => { if (readyTimer.current) window.clearTimeout(readyTimer.current); };
  }, [share]);

  if (err) return <div style={{ padding: 24, color: '#b84b2a' }}>Partage indisponible : {err}</div>;
  if (!share) return <div style={{ padding: 24 }}>Chargement…</div>;

  if (share.kind !== 'presentation') {
    return (
      <div style={{ padding: 24 }}>
        Cette page d'impression ne supporte que les présentations. Type reçu : {share.kind}.
      </div>
    );
  }

  const p = share.payload?.presentation;
  if (!p) return <div style={{ padding: 24 }}>Présentation indisponible.</div>;

  const theme: ThemeId = normalizeThemeId(p.theme);
  const orientation = (p.orientation === 'landscape' ? 'landscape' : 'portrait') as
    | 'portrait'
    | 'landscape';
  const customStyle: React.CSSProperties | undefined =
    theme === 'custom'
      ? ({
          '--custom-accent': p.custom?.accent ?? '#3D5A45',
          '--custom-font': getFont(p.custom?.font),
        } as React.CSSProperties)
      : undefined;

  return (
    <div className="print-root" data-orientation={orientation}>
      <div className="print-cover">
        <div className="print-cover-title">{share.title ?? p.title ?? 'Synthèse'}</div>
        <div className="print-cover-meta">
          Projet {share.project_name}
          {share.author_name ? ` · ${share.author_name}` : ''}
          {' · '}
          {new Date().toLocaleDateString('fr-CH', { day: '2-digit', month: 'long', year: 'numeric' })}
        </div>
      </div>

      <div
        className={`pres-theme-wrap ${themeClass(theme)}`}
        data-orientation={orientation}
        style={customStyle}
      >
        <div className="presentation-slides">
          {(p.sections ?? []).map((s: any, i: number) => {
            return (
              <div
                key={s.id ?? i}
                className={`presentation-slide ${spanToClass(s.width)} ${s.type === 'hero' ? 'slide-hero' : ''} avoid-break`}
                data-section-idx={String(i + 1).padStart(2, '0')}
                data-span={String(typeof s.width === 'number' ? s.width : s.width === 'half' ? 6 : s.width === 'third' ? 4 : 12)}
              >
                <SlideContent section={s} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
